import { GlideFC } from "lib/glide-fc";
import Img from "../../smart/img/img";
import { HeroProps } from "./hero-lib";
import { Appearance } from "common/generator/components/button-appearance";
import { styled } from "webapp/styled-components";
import tw from "twin.macro";
import { WireButton } from "../wire-button/wire-button";
import classNames from "classnames";
import chroma from "chroma-js";
import { massageImageUrl } from "common/components/portable-renderers";
import { GlideIcon } from "sharedUI/ui/views/glide-icon/glide-icon";
import { isEmptyOrUndefined } from "common/Support";

const Style = styled.div`
    .start {
        text-align: start;
        justify-content: flex-start;
    }

    .center {
        text-align: center;
        justify-content: center;
    }

    .end {
        text-align: end;
        justify-content: flex-end;
    }

    --hero-fg: ${p => p.theme.textDark};
    --hero-accent-fg: ${p => p.theme.primaryAccentColor};

    &.style-dark {
        --hero-fg: white;
    }

    &.style-card {
        ${tw`px-6 rounded-lg shadow-md bg-n0`}
    }

    &.style-accent {
        --hero-fg: white;
        --hero-accent-fg: white;
    }

    &.style-image.darken,
    &.style-image.none {
        --hero-fg: white;
        --hero-accent-fg: ${p => chroma(p.theme.primaryAccentColor).brighten(1).css()};
    }

    &.style-image.lighten {
        --hero-fg: black;
        --hero-accent-fg: ${p => chroma(p.theme.primaryAccentColor).darken(0.5).css()};
    }

    &.style-image.none .text-block {
        text-shadow: 0px 2px 8px rgba(0, 0, 0, 0.35), 0px 1px 2px rgba(0, 0, 0, 0.5);
    }

    .text-title {
        color: var(--hero-fg);
    }

    .text-accent {
        color: var(--hero-accent-fg);
    }

    .text-description {
        color: var(--hero-fg);
    }

    .buttons-box {
        display: grid;
        grid-template-columns: 1fr auto;
        justify-content: center;
        gap: 8px;

        > * {
            min-width: 100px;
        }

        .primary-button {
            grid-column: 1/3;
        }
    }

    &.no-menu .buttons-box {
        grid-template-columns: 1fr;

        .primary-button {
            grid-column: auto;
        }
    }

    .gp-sm & .buttons-box {
        grid-template-columns: 1fr 1fr auto;

        .primary-button {
            grid-column: auto;
        }
    }

    .gp-sm &.no-menu .buttons-box {
        grid-template-columns: 1fr 1fr;

        .primary-button {
            grid-column: auto;
        }
    }

    .gp-sm &.no-secondary .buttons-box {
        grid-template-columns: 1fr auto;
    }

    .gp-sm &.no-secondary.no-menu .buttons-box {
        grid-template-columns: 1fr;
    }

    &.no-secondary .primary-button,
    &.no-secondary.no-menu .primary-button {
        grid-column: auto;
    }

    &.no-secondary.no-menu .primary-button {
        grid-column: auto;
    }

    .gp-xl.gp-xl.gp-xl.gp-xl & .buttons-box {
        justify-content: flex-end;
    }
`;

const Hero: GlideFC<HeroProps> = p => {
    const {
        emphasizedTitle,
        title,
        description,
        style,
        titleAlignment,
        emphasizedTitleAlignment,
        backgroundEffect,
        iconImageURL,
        primaryText,
        primaryOnClick,
        secondaryText,
        secondaryOnClick,
        menuItems,
    } = p;

    const hasSecondary = !isEmptyOrUndefined(secondaryText) && secondaryOnClick !== undefined;
    const hasMenu = menuItems !== undefined && menuItems.length > 0;

    return (
        <Style
            className={classNames(
                `style-${style?.toLowerCase()}`,
                backgroundEffect?.toLowerCase(),
                !hasSecondary && "no-secondary",
                !hasMenu && "no-menu"
            )}
            tw="relative py-5 px-2 flex flex-col flex-grow font-extrabold text-2xl
                gp-sm:py-5
                gp-md:(py-6 text-3xl rounded-xl)
                gp-lg:(py-7 flex-row items-center)
                gp-xl:(py-8 text-4xl)">
            <div
                className="icon-text-layout"
                tw="flex items-center flex-grow
                    gp-lg:mr-8">
                {iconImageURL && (
                    <Img
                        src={massageImageUrl(
                            iconImageURL,
                            {
                                thumbnail: false,
                                width: 144,
                                height: 144,
                            },
                            undefined
                        )}
                        tw="width[72px] height[72px] flex-shrink-0 self-start object-cover rounded-lg
                            gp-md:(w-24 h-24)
                            gp-lg:(w-28 h-28)
                            gp-xl:(w-36 h-36)"
                    />
                )}
                <div className="text-stack" tw="flex flex-col not-first:ml-4">
                    {emphasizedTitle && (
                        <h3
                            tw="font-bold font-size[12px] line-height[24px] uppercase"
                            className={classNames(emphasizedTitleAlignment, "text-accent", "text-block")}>
                            {emphasizedTitle}
                        </h3>
                    )}
                    {title && <h2 className={classNames(titleAlignment, "text-title", "text-block")}>{title}</h2>}
                    {description && (
                        <p className="text-description text-block" tw="font-normal text-sm whitespace-pre-wrap">
                            {description}
                        </p>
                    )}
                </div>
            </div>
            <div className="buttons-box">
                {primaryText && primaryOnClick && (
                    <WireButton className="primary-button" appearance={Appearance.Filled} onClick={primaryOnClick}>
                        {primaryText}
                    </WireButton>
                )}
                {hasSecondary && (
                    <WireButton appearance={Appearance.Bordered} onClick={secondaryOnClick}>
                        {secondaryText}
                    </WireButton>
                )}
                {hasMenu && (
                    <WireButton appearance={Appearance.Bordered} tw="min-w-min! max-w-min px-2!">
                        <GlideIcon icon="mt-dots-horizontal" kind="monotone" iconSize={20} />
                    </WireButton>
                )}
            </div>
        </Style>
    );
};

export default Hero;
